import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { productUrl } from '../utils/urls';
import { optimizeImageUrl } from '../utils/imageUrl';
import { useTracking } from '../hooks/useTracking';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  ChevronRight, 
  Package,
  Loader2,
  ArrowRight,
  Filter,
  ShoppingBag,
  Swords,
  Sparkles,
  Key,
  Heart,
  Gem,
  Armchair,
  Palette,
  Mountain,
  Shirt,
  Baby,
  Church,
  GraduationCap,
  Slice,
  Frame,
  Hammer,
  Lamp,
  Box,
  TreeDeciduous,
  Printer,
  Briefcase,
  Scissors,
  Flower2
} from 'lucide-react';
import { motion } from 'framer-motion';
import { calculateFinalPrice, calculateFullPrice, getTotalDiscountPercentage } from '../lib/feeCalculator';
import { sortProductsByAvailability, getProductAvailability } from '../utils/productAvailability';
import { getChildCategoryName } from '../utils/categoryPath';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback images for categories without database image
const FALLBACK_IMAGES = {
  'donna': 'https://images.unsplash.com/photo-1557161622-5f50ca344787?w=600&q=80',
  'uomo': 'https://images.unsplash.com/photo-1741709847860-85286566b5c9?w=600&q=80',
  'bambino': 'https://images.pexels.com/photos/15704149/pexels-photo-15704149.jpeg?w=600&q=80',
  'accessori': 'https://images.unsplash.com/photo-1606760227091-3dd870d97f1d?w=600&q=80',
  'orologi': 'https://images.unsplash.com/photo-1749831754129-3a84b9fdeb87?w=600&q=80',
  'bracciali': 'https://images.unsplash.com/photo-1767921777873-81818b812a4d?w=600&q=80',
  'anelli': 'https://images.pexels.com/photos/2732096/pexels-photo-2732096.jpeg?w=600&q=80',
  'orecchini': 'https://images.unsplash.com/photo-1771173652661-8245a9d94095?w=600&q=80',
  'collane': 'https://images.unsplash.com/photo-1758995115560-59c10d6cc28f?w=600&q=80',
  'pittura': 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=600&q=80',
  'scultura': 'https://images.unsplash.com/photo-1544531586-fde5298cdd40?w=600&q=80',
  'ceramiche': 'https://images.unsplash.com/photo-1565193566173-7a0ee3dbe261?w=600&q=80',
  'portachiavi': 'https://images.unsplash.com/photo-1622838320226-2d88bffa4a7c?w=600&q=80',
  'portapenne': 'https://images.unsplash.com/photo-1583485088034-697b5bc54ccd?w=600&q=80',
  'segnalibri': 'https://images.unsplash.com/photo-1456735190827-d1262f71b8a3?w=600&q=80',
  'arredamento': 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600&q=80',
  'illuminazione': 'https://images.unsplash.com/photo-1507473885765-e6ed057f782c?w=600&q=80',
  'bagno': 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=600&q=80',
  'cucina': 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&q=80',
  'tessuti': 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=600&q=80',
  'feste': 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600&q=80',
  'lauree': 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=600&q=80',
  'matrimoni': 'https://images.unsplash.com/photo-1519741497674-611481863552?w=600&q=80',
  'battesimi': 'https://images.pexels.com/photos/16537217/pexels-photo-16537217.png?w=600&q=80',
  'comunioni-cresime': 'https://images.unsplash.com/photo-1746559676407-370f34bec476?w=600&q=80',
  'giochi': 'https://images.unsplash.com/photo-1558060370-d644479cb6f7?w=600&q=80',
  'musica': 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=600&q=80',
  'libri': 'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=600&q=80',
  'skincare': 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=600&q=80',
  'capelli': 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=600&q=80',
  'profumi': 'https://images.unsplash.com/photo-1541643600914-78b084683601?w=600&q=80',
};

// Default fallback image
const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=600&q=80';

// Default SEO metadata - fallback if not set in database
const DEFAULT_CATEGORY_SEO = {
  'abbigliamento': {
    title: 'Abbigliamento Artigianale Made in Italy | Rarifex',
    h1: 'Abbigliamento Artigianale Italiano',
    intro: 'Esplora la nostra selezione di capi di abbigliamento realizzati a mano da artigiani italiani.'
  },
  'gioielli': {
    title: 'Gioielli Artigianali Made in Italy | Rarifex',
    h1: 'Gioielli Artigianali Italiani',
    intro: 'Esplora la nostra selezione di gioielli realizzati a mano da maestri orafi italiani.'
  },
  'arte': {
    title: 'Arte Artigianale Made in Italy | Rarifex',
    h1: 'Arte Artigianale Italiana',
    intro: 'Scopri opere d\'arte uniche create da artisti e artigiani italiani.'
  },
  'oggetti': {
    title: 'Oggetti Artigianali Made in Italy | Rarifex',
    h1: 'Oggetti Artigianali Italiani',
    intro: 'Esplora la nostra selezione di oggetti artigianali realizzati a mano.'
  },
  'casa': {
    title: 'Casa e Arredamento Artigianale | Rarifex',
    h1: 'Casa e Arredamento Artigianale',
    intro: 'Arreda la tua casa con pezzi unici realizzati da artigiani italiani.'
  },
  'eventi': {
    title: 'Eventi e Cerimonie | Rarifex',
    h1: 'Articoli per Eventi e Cerimonie',
    intro: 'Scopri bomboniere, inviti e decorazioni artigianali per i tuoi eventi speciali.'
  },
  'intrattenimento': {
    title: 'Intrattenimento Artigianale | Rarifex',
    h1: 'Intrattenimento Artigianale',
    intro: 'Giochi, musica e libri artigianali per il tuo tempo libero.'
  },
  'bellezza': {
    title: 'Bellezza Artigianale Made in Italy | Rarifex',
    h1: 'Bellezza Artigianale Italiana',
    intro: 'Prodotti di bellezza naturali e artigianali per la tua cura personale.'
  }
};

export default function CategoryLandingPage() {
  const { category, subcategory, subsubcategory, subsubsubcategory } = useParams();
  const { trackCategoryView } = useTracking();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subcategoryCounts, setSubcategoryCounts] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [dbCategoryData, setDbCategoryData] = useState(null);
  const [dbSubcategories, setDbSubcategories] = useState([]);
  const [allDbCategories, setAllDbCategories] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [isLaunchMode, setIsLaunchMode] = useState(false);
  const [launchModeLoaded, setLaunchModeLoaded] = useState(false);

  // Load launch mode status
  useEffect(() => {
    const loadLaunchMode = async () => {
      try {
        const response = await fetch(`${API_URL}/api/settings/launch-mode`);
        if (response.ok) {
          const data = await response.json();
          const isActive = data.launch_mode === true;
          setIsLaunchMode(isActive);
          // Sync to localStorage
          localStorage.setItem('rarifex_launch_mode', isActive.toString());
        }
      } catch (error) {
        // Fallback to localStorage
        setIsLaunchMode(localStorage.getItem('rarifex_launch_mode') === 'true');
      } finally {
        setLaunchModeLoaded(true);
      }
    };
    loadLaunchMode();
  }, []);

  // Track category view
  useEffect(() => {
    const categorySlug = subsubsubcategory || subsubcategory || subcategory || category;
    if (categorySlug) {
      trackCategoryView(categorySlug);
    }
  }, [category, subcategory, subsubcategory, subsubsubcategory, trackCategoryView]);

  // Fetch all categories from database
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/categories/public`);
        if (response.ok) {
          const data = await response.json();
          if (data.categories) {
            setAllDbCategories(data.categories);
          }
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setCategoriesLoaded(true);
      }
    };
    fetchCategories();
  }, []);

  // Find current category and its subcategories from database
  const mainCategory = useMemo(() => {
    return allDbCategories.find(c => c.slug === category && !c.parent_id);
  }, [allDbCategories, category]);

  const subcategoryInfo = useMemo(() => {
    if (!subcategory) return null;
    return allDbCategories.find(c => c.slug === subcategory && c.parent_id);
  }, [allDbCategories, subcategory]);

  // Find 3rd level category info
  const subsubcategoryInfo = useMemo(() => {
    if (!subsubcategory || !subcategoryInfo) return null;
    return allDbCategories.find(c => c.slug === subsubcategory && c.parent_id === subcategoryInfo.id);
  }, [allDbCategories, subsubcategory, subcategoryInfo]);

  // Find 4th level category info
  const subsubsubcategoryInfo = useMemo(() => {
    if (!subsubsubcategory || !subsubcategoryInfo) return null;
    return allDbCategories.find(c => c.slug === subsubsubcategory && c.parent_id === subsubcategoryInfo.id);
  }, [allDbCategories, subsubsubcategory, subsubcategoryInfo]);

  // Get current category image (prioritize most specific: 4th > 3rd > 2nd > main)
  const categoryImage = useMemo(() => {
    if (subsubsubcategoryInfo?.image_url) return subsubsubcategoryInfo.image_url;
    if (subsubcategoryInfo?.image_url) return subsubcategoryInfo.image_url;
    if (subcategoryInfo?.image_url) return subcategoryInfo.image_url;
    if (mainCategory?.image_url) return mainCategory.image_url;
    return null;
  }, [subsubsubcategoryInfo, subsubcategoryInfo, subcategoryInfo, mainCategory]);

  // Get subcategories to display (children of current level)
  const displaySubcategories = useMemo(() => {
    if (!mainCategory) return [];
    
    if (subsubsubcategory && subsubsubcategoryInfo) {
      // At 4th level - no more children to show (could extend if needed)
      return [];
    } else if (subsubcategory && subsubcategoryInfo) {
      // At 3rd level - check for 4th level children
      const fourthLevelChildren = allDbCategories
        .filter(c => c.parent_id === subsubcategoryInfo.id)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map(sub => ({
          id: sub.slug,
          name: sub.name,
          image_url: sub.image_url,
          icon: sub.icon
        }));
      return fourthLevelChildren;
    } else if (subcategory && subcategoryInfo) {
      // At subcategory (2nd) level - show its 3rd level children
      return allDbCategories
        .filter(c => c.parent_id === subcategoryInfo.id)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map(sub => ({
          id: sub.slug,
          name: sub.name,
          image_url: sub.image_url,
          icon: sub.icon
        }));
    } else {
      // At main category level - show its subcategories
      return allDbCategories
        .filter(c => c.parent_id === mainCategory.id)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map(sub => ({
          id: sub.slug,
          name: sub.name,
          image_url: sub.image_url,
          icon: sub.icon
        }));
    }
  }, [allDbCategories, mainCategory, subcategory, subcategoryInfo, subsubcategory, subsubcategoryInfo, subsubsubcategory, subsubsubcategoryInfo]);

  // Get all descendant category names for querying products
  const descendantNames = useMemo(() => {
    if (!mainCategory) return [];
    
    const names = [];
    
    if (subsubsubcategory && subsubsubcategoryInfo) {
      // At 4th level - just this specific category
      names.push(subsubsubcategoryInfo.name);
    } else if (subsubcategory && subsubcategoryInfo) {
      // At 3rd level - include this category + any 4th level children
      names.push(subsubcategoryInfo.name);
      // Also add 4th level children if they exist
      allDbCategories
        .filter(c => c.parent_id === subsubcategoryInfo.id)
        .forEach(child => names.push(child.name));
    } else if (subcategory && subcategoryInfo) {
      // At 2nd level - include this subcategory + its children + 4th level
      names.push(subcategoryInfo.name);
      const thirdLevel = allDbCategories.filter(c => c.parent_id === subcategoryInfo.id);
      thirdLevel.forEach(child => {
        names.push(child.name);
        // Also add 4th level children
        allDbCategories
          .filter(c => c.parent_id === child.id)
          .forEach(grandchild => names.push(grandchild.name));
      });
    } else {
      // At main category level - include main + all levels
      names.push(mainCategory.name);
      const subcats = allDbCategories.filter(c => c.parent_id === mainCategory.id);
      subcats.forEach(sub => {
        names.push(sub.name);
        // Also add 3rd level children
        const thirdLevel = allDbCategories.filter(c => c.parent_id === sub.id);
        thirdLevel.forEach(child => {
          names.push(child.name);
          // Also add 4th level children
          allDbCategories
            .filter(c => c.parent_id === child.id)
            .forEach(grandchild => names.push(grandchild.name));
        });
      });
    }
    
    return names;
  }, [allDbCategories, mainCategory, subcategory, subcategoryInfo, subsubcategory, subsubcategoryInfo, subsubsubcategory, subsubsubcategoryInfo]);

  // Current display name for SEO
  const currentName = subsubsubcategory 
    ? (subsubsubcategoryInfo?.name || subsubsubcategory)
    : subsubcategory 
      ? (subsubcategoryInfo?.name || subsubcategory)
      : subcategory 
        ? (subcategoryInfo?.name || subcategory)
        : (mainCategory?.name || category);

  // Get SEO data from DB or defaults
  const seoData = useMemo(() => {
    const dbData = subsubsubcategory ? subsubsubcategoryInfo : (subsubcategory ? subsubcategoryInfo : (subcategory ? subcategoryInfo : mainCategory));
    const defaultSeo = DEFAULT_CATEGORY_SEO[category] || {};
    
    return {
      title: dbData?.seo_title || defaultSeo.title || `${currentName} | Rarifex`,
      h1: dbData?.seo_title?.replace(' | Rarifex', '') || defaultSeo.h1 || currentName,
      intro: dbData?.seo_intro || defaultSeo.intro || `Esplora la nostra selezione di ${currentName.toLowerCase()}.`,
      footerTitle: dbData?.seo_footer_title || `${currentName} Artigianale Italiano`,
      footerDescription: dbData?.seo_description || null,
      image: dbData?.image_url || null
    };
  }, [mainCategory, subcategoryInfo, subsubcategoryInfo, subsubsubcategoryInfo, subcategory, subsubcategory, subsubsubcategory, category, currentName]);

  // Build breadcrumb
  const breadcrumbs = useMemo(() => {
    const crumbs = [
      { name: 'Home', path: '/' },
    ];
    
    if (category && mainCategory) {
      crumbs.push({ 
        name: mainCategory.name, 
        path: `/categoria/${category}` 
      });
    }
    
    if (subcategory && subcategoryInfo) {
      crumbs.push({ 
        name: subcategoryInfo.name, 
        path: `/categoria/${category}/${subcategory}` 
      });
    }
    
    if (subsubcategory && subsubcategoryInfo) {
      crumbs.push({ 
        name: subsubcategoryInfo.name, 
        path: `/categoria/${category}/${subcategory}/${subsubcategory}` 
      });
    }
    
    if (subsubsubcategory && subsubsubcategoryInfo) {
      crumbs.push({ 
        name: subsubsubcategoryInfo.name, 
        path: `/categoria/${category}/${subcategory}/${subsubcategory}/${subsubsubcategory}` 
      });
    }
    
    return crumbs;
  }, [category, subcategory, subsubcategory, subsubsubcategory, mainCategory, subcategoryInfo, subsubcategoryInfo, subsubsubcategoryInfo]);

  // Load products and counts - only after categories are loaded
  useEffect(() => {
    document.title = seoData.title;
    
    // Wait for categories to load before querying products
    if (!categoriesLoaded) return;
    
    loadProducts();
    loadSubcategoryCounts();
  }, [category, subcategory, subsubcategory, subsubsubcategory, descendantNames, categoriesLoaded, seoData.title]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      if (!categoriesLoaded) {
        console.log('Categories not loaded yet');
        setProducts([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }

      if (!mainCategory) {
        console.log('Main category not found for slug:', category);
        setProducts([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }

      // Legacy category name mapping (old product data -> new category names)
      const LEGACY_CATEGORY_MAP = {
        'Abbigliamento e Accessori': ['Abbigliamento', 'Clothing', 'Fashion', 'Accessori'],
        'Gioielli': ['Jewelry', 'Jewellery', 'Bijoux'],
        'Arte': ['Art', 'Pittura', 'Scultura', 'Ceramica', 'Ceramiche'],
        'Oggetti': ['Objects', 'Gadgets'],
        'Casa': ['Home', 'Mobili', 'Furniture', 'Arredamento'],
        'Eventi': ['Events', 'Party', 'Wedding'],
        'Intrattenimento': ['Entertainment', 'Games', 'Giochi'],
        'Bellezza': ['Beauty', 'Cosmetics', 'Skincare'],
      };

      // Get all possible names for this category
      const categoryNames = [mainCategory.name];
      const legacyNames = LEGACY_CATEGORY_MAP[mainCategory.name] || [];
      categoryNames.push(...legacyNames);

      console.log('Searching for category names:', categoryNames);
      console.log('Subcategory:', subcategory, 'SubcategoryInfo:', subcategoryInfo?.name);

      // First, get all active products with profile data
      const { data: allData, error: allError } = await supabase
        .from('products')
        .select(`
          *,
          profiles:crafter_id (
            id,
            full_name,
            avatar_url,
            tier
          )
        `)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (allError) {
        console.error('Error fetching products:', allError);
        setProducts([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }

      console.log('Total products fetched:', allData?.length || 0);

      // Filter client-side for matching categories
      const filteredProducts = (allData || []).filter(product => {
        const productCategory = (product.category || '').toLowerCase();
        const categoryPath = product.customization_options?.categoryPath;
        
        // If we're on a 3rd level (subsubcategory) page, filter strictly by it
        if (subsubcategory && subsubcategoryInfo) {
          const subsubName = subsubcategoryInfo.name.toLowerCase();
          
          // Check if product has categoryPath with matching child (3rd level)
          // Retrocompat: `child` può essere stringa o oggetto {name, slug}
          const childName = typeof categoryPath?.child === 'object'
            ? categoryPath.child?.name
            : categoryPath?.child;
          if (childName) {
            return String(childName).toLowerCase() === subsubName;
          }
          
          // Fallback: check if category field contains the subsubcategory name
          return productCategory.includes(subsubName);
        }
        
        // If we're on a subcategory (2nd level) page, filter by subcategory + its children
        if (subcategory && subcategoryInfo) {
          const subcategoryName = subcategoryInfo.name.toLowerCase();
          
          // Check if product has categoryPath with matching sub
          if (categoryPath && categoryPath.sub) {
            // Match either exact sub OR has a child under this sub
            if (categoryPath.sub.toLowerCase() === subcategoryName) {
              return true;
            }
          }
          
          // Fallback: check if category field contains the subcategory name
          return productCategory.includes(subcategoryName);
        }
        
        // On main category page - show all products from this category AND its subcategories
        // First check the main category
        const matchesMainCategory = categoryNames.some(catName => 
          productCategory.includes(catName.toLowerCase())
        );
        
        if (matchesMainCategory) return true;
        
        // Also check if product's categoryPath.main matches
        if (categoryPath && categoryPath.main) {
          return categoryPath.main.toLowerCase() === mainCategory.name.toLowerCase() ||
                 categoryPath.main.toLowerCase() === mainCategory.slug?.toLowerCase();
        }
        
        return false;
      });

      console.log('Products found after filtering:', filteredProducts.length);
      
      // Apply availability sorting (out-of-stock products at the end), then take first 12
      const sortedProducts = sortProductsByAvailability(filteredProducts, (a, b) => new Date(b.created_at) - new Date(a.created_at));
      setProducts(sortedProducts.slice(0, 12));
      setTotalCount(filteredProducts.length);
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSubcategoryCounts = async () => {
    if (!categoriesLoaded || displaySubcategories.length === 0) return;

    const counts = {};

    try {
      // Fetch active products once and count client-side aggregating descendant leaves.
      // I prodotti sono salvati SOLO nelle foglie (categoryPath.child = 3° livello),
      // con categoryPath.sub = 2° livello. Le categorie madre aggregano i figli.
      const { data, error } = await supabase
        .from('products')
        .select('category, customization_options')
        .eq('status', 'active');

      if (error) throw error;
      const prods = data || [];

      // Livello delle card mostrate: main(2° lv) / subcategory(3° lv) / subsubcategory(4° lv)
      const displayLevel = subsubcategory ? 4 : subcategory ? 3 : 2;

      for (const sub of displaySubcategories) {
        const name = (sub.name || '').toLowerCase();
        counts[sub.id] = prods.filter((p) => {
          const cp = p.customization_options?.categoryPath;
          if (displayLevel === 2) {
            // Card di 2° livello: conta tutti i prodotti con sub corrispondente
            // (aggrega automaticamente le foglie di 3° livello sottostanti)
            if (cp?.sub) return String(cp.sub).toLowerCase() === name;
            return (p.category || '').toLowerCase().includes(name);
          }
          // Card di 3°/4° livello (foglie): conta per categoryPath.child
          const childName = typeof cp?.child === 'object' ? cp?.child?.name : cp?.child;
          if (childName) return String(childName).toLowerCase() === name;
          return (p.category || '').toLowerCase().includes(name);
        }).length;
      }
    } catch (error) {
      console.error('Error loading subcategory counts:', error);
    }

    setSubcategoryCounts(counts);
  };

  const getSubcategoryLink = (subId, subName) => {
    // If we're on a 3rd level category, link to 4th level
    if (subsubcategory) {
      return `/categoria/${category}/${subcategory}/${subsubcategory}/${subId}`;
    }
    // If we're on a subcategory page, link to 3rd level
    if (subcategory) {
      return `/categoria/${category}/${subcategory}/${subId}`;
    }
    // Otherwise link to 2nd level
    return `/categoria/${category}/${subId}`;
  };

  const getSubcategoryImage = (sub) => {
    // Priority: 1. Database image_url, 2. Fallback by slug, 3. Default
    const base = (sub.image_url && sub.image_url.trim())
      ? sub.image_url
      : (FALLBACK_IMAGES[sub.id] || FALLBACK_IMAGES[sub.name?.toLowerCase()] || DEFAULT_IMAGE);
    return optimizeImageUrl(base, 600);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* SEO-friendly header section with optional background image */}
      <section 
        className="relative text-white py-12 overflow-hidden"
        style={{
          background: categoryImage 
            ? undefined 
            : 'linear-gradient(to bottom right, #008080, #006666)'
        }}
      >
        {/* Background gradient fallback */}
        {!categoryImage && (
          <div className="absolute inset-0 bg-gradient-to-br from-[#008080] to-[#006666]" />
        )}
        
        {/* Background image with gradient overlay (like HomePage hero) */}
        {categoryImage && (
          <div 
            className="absolute inset-0 animate-fadeIn"
            style={{
              backgroundImage: `linear-gradient(135deg, rgba(0, 100, 100, 0.85), rgba(0, 80, 80, 0.88)), url('${categoryImage}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
        )}
        
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          .animate-fadeIn {
            animation: fadeIn 0.5s ease-in-out;
          }
        `}</style>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-white/70 text-sm mb-6" aria-label="Breadcrumb">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.path}>
                {index > 0 && <ChevronRight className="w-4 h-4" />}
                {index === breadcrumbs.length - 1 ? (
                  <span className="text-white font-medium">{crumb.name}</span>
                ) : (
                  <Link to={crumb.path} className="hover:text-white transition-colors">
                    {crumb.name}
                  </Link>
                )}
              </React.Fragment>
            ))}
          </nav>

          {/* H1 and intro text for SEO */}
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{seoData.h1}</h1>
          <p className="text-lg text-white/90 max-w-3xl">{seoData.intro}</p>
        </div>
      </section>

      {/* Cosplay Custom Request CTA Banner */}
      {category === 'cosplay' && (
        <section className="bg-gradient-to-r from-purple-600 to-indigo-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Swords className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Hai un progetto cosplay in mente?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Descrivi il tuo personaggio e ricevi proposte da sarti, armaioli e prop maker italiani
                  </p>
                </div>
              </div>
              <Link to="/cosplay-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-purple-600 hover:bg-purple-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="cosplay-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Richiedi cosplay su misura
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Matrimonio Custom Request CTA Banner */}
      {(category === 'matrimoni' || subcategory === 'matrimoni') && (
        <section className="bg-gradient-to-r from-amber-500 to-rose-400 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Heart className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">State organizzando il vostro matrimonio?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Bomboniere, inviti, tableau e decorazioni artigianali Made in Italy
                  </p>
                </div>
              </div>
              <Link to="/matrimonio-artigianale">
                <Button 
                  size="lg" 
                  className="bg-white text-amber-600 hover:bg-amber-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="matrimonio-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Richiedi per il tuo matrimonio
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Gioielli Custom Request CTA Banner */}
      {category === 'gioielli' && (
        <section className="bg-gradient-to-r from-teal-500 to-amber-500 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Gem className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi un gioiello unico e personalizzato?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Descrivi la tua idea e ricevi proposte da maestri orafi italiani
                  </p>
                </div>
              </div>
              <Link to="/gioielli-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-teal-600 hover:bg-teal-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="gioielli-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Crea il tuo gioiello su misura
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Arredamento Custom Request CTA Banner */}
      {(subcategory === 'arredamento' || category === 'arredamento') && (
        <section className="bg-gradient-to-r from-orange-600 to-amber-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Armchair className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi un mobile su misura per la tua casa?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Descrivi il tuo progetto e ricevi proposte da maestri falegnami italiani
                  </p>
                </div>
              </div>
              <Link to="/arredamento-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-orange-600 hover:bg-orange-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="arredamento-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Crea il tuo mobile su misura
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Pittura Custom Request CTA Banner */}
      {(category === 'pittura' || subcategory === 'pittura') && (
        <section className="bg-gradient-to-r from-indigo-600 to-purple-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Palette className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Vuoi commissionare un dipinto unico?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Ritratti, paesaggi, astratti: descrivi la tua idea e ricevi proposte da artisti italiani
                  </p>
                </div>
              </div>
              <Link to="/dipinti-su-commissione">
                <Button 
                  size="lg" 
                  className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="pittura-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Commissiona il tuo dipinto
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Scultura Custom Request CTA Banner */}
      {(category === 'scultura' || subcategory === 'scultura') && (
        <section className="bg-gradient-to-r from-stone-700 to-amber-700 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Mountain className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Vuoi commissionare una scultura unica?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Busti, statue, opere astratte: descrivi la tua visione e ricevi proposte da scultori italiani
                  </p>
                </div>
              </div>
              <Link to="/sculture-su-commissione">
                <Button 
                  size="lg" 
                  className="bg-white text-stone-700 hover:bg-stone-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="scultura-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Commissiona la tua scultura
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Portachiavi Custom Request CTA Banner */}
      {(subcategory === 'portachiavi' || category === 'portachiavi') && (
        <section className="bg-gradient-to-r from-emerald-600 to-teal-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Key className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi un portachiavi personalizzato?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Incisioni, foto, materiali pregiati: descrivi la tua idea e ricevi proposte da artigiani italiani
                  </p>
                </div>
              </div>
              <Link to="/portachiavi-personalizzati">
                <Button 
                  size="lg" 
                  className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="portachiavi-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Crea il tuo portachiavi
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Camicie Custom Request CTA Banner */}
      {(subsubcategory === 'camicie' || subcategory === 'camicie' || category === 'camicie') && (
        <section className="bg-gradient-to-r from-slate-600 to-blue-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Shirt className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi una camicia su misura?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Tessuti pregiati, colli e polsini personalizzati: descrivi la tua idea e ricevi proposte da sarti italiani
                  </p>
                </div>
              </div>
              <Link to="/camicie-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-slate-700 hover:bg-slate-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="camicie-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Crea la tua camicia su misura
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Portafogli Custom Request CTA Banner */}
      {(subsubcategory === 'portafogli' || subcategory === 'portafogli' || category === 'portafogli') && (
        <section className="bg-gradient-to-r from-amber-600 to-orange-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Gem className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi un portafoglio in pelle su misura?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Pelle italiana, cuciture a mano, iniziali incise: descrivi la tua idea e ricevi proposte da pellettieri artigiani
                  </p>
                </div>
              </div>
              <Link to="/portafogli-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-amber-700 hover:bg-amber-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="portafogli-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Crea il tuo portafoglio su misura
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Cinture Custom Request CTA Banner */}
      {(subsubcategory === 'cinture' || subcategory === 'cinture' || category === 'cinture') && (
        <section className="bg-gradient-to-r from-stone-600 to-amber-700 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Key className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi una cintura in pelle su misura?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Cuoio italiano, fibbie artigianali, iniziali incise: descrivi la tua idea e ricevi proposte da pellettieri artigiani
                  </p>
                </div>
              </div>
              <Link to="/cinture-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-stone-700 hover:bg-stone-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="cinture-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Crea la tua cintura su misura
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Pelletteria Generic CTA Banner */}
      {(subcategory === 'pelletteria' && !subsubcategory) && (
        <section className="bg-gradient-to-r from-amber-600 to-orange-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Briefcase className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi un articolo in pelle su misura?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Borse, portafogli, cinture e accessori: descrivi la tua idea e ricevi proposte da artigiani pellettieri italiani
                  </p>
                </div>
              </div>
              <Link to="/pelletteria-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-amber-600 hover:bg-amber-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="pelletteria-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Crea il tuo articolo in pelle
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Pelletteria subcategories CTA (for sub-subcategories without dedicated landing) */}
      {subcategory === 'pelletteria' && subsubcategory && !['portafogli', 'cinture'].includes(subsubcategory) && (
        <section className="bg-gradient-to-r from-amber-600 to-orange-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Briefcase className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Vuoi un articolo in pelle personalizzato?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Descrivi la tua idea e ricevi proposte da artigiani pellettieri italiani
                  </p>
                </div>
              </div>
              <Link to="/pelletteria-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-amber-600 hover:bg-amber-50 font-semibold shadow-lg whitespace-nowrap"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Crea il tuo articolo in pelle
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Ceramiche Generic CTA Banner */}
      {(subcategory === 'ceramiche' && !subsubcategory) && (
        <section className="bg-gradient-to-r from-sky-600 to-blue-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Flower2 className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi ceramiche artigianali su misura?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Maioliche, vasi, piatti e decorazioni: descrivi la tua idea e ricevi proposte da ceramisti italiani
                  </p>
                </div>
              </div>
              <Link to="/ceramiche-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-sky-600 hover:bg-sky-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="ceramiche-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Crea la tua ceramica
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Ceramiche subcategories CTA */}
      {subcategory === 'ceramiche' && subsubcategory && (
        <section className="bg-gradient-to-r from-sky-600 to-blue-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Flower2 className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Vuoi una ceramica personalizzata?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Descrivi la tua idea e ricevi proposte da ceramisti italiani
                  </p>
                </div>
              </div>
              <Link to="/ceramiche-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-sky-600 hover:bg-sky-50 font-semibold shadow-lg whitespace-nowrap"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Crea la tua ceramica
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Lavorazione Vetro Generic CTA Banner */}
      {(subcategory === 'lavorazione-vetro' && !subsubcategory) && (
        <section className="bg-gradient-to-r from-cyan-600 to-teal-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Gem className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi vetro artistico su misura?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Lampadari, vetrate, sculture in vetro soffiato: descrivi la tua idea e ricevi proposte da maestri vetrai italiani
                  </p>
                </div>
              </div>
              <Link to="/vetro-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-cyan-600 hover:bg-cyan-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="vetro-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Crea la tua opera in vetro
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Lavorazione Vetro subcategories CTA */}
      {subcategory === 'lavorazione-vetro' && subsubcategory && (
        <section className="bg-gradient-to-r from-cyan-600 to-teal-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Gem className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Vuoi un'opera in vetro personalizzata?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Descrivi la tua idea e ricevi proposte da maestri vetrai italiani
                  </p>
                </div>
              </div>
              <Link to="/vetro-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-cyan-600 hover:bg-cyan-50 font-semibold shadow-lg whitespace-nowrap"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Crea la tua opera in vetro
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Feste Custom Request CTA Banner */}
      {(subsubcategory === 'feste' || subcategory === 'feste' || category === 'feste') && (
        <section className="bg-gradient-to-r from-pink-500 to-purple-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Heart className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi decorazioni uniche per la tua festa?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Inviti, bomboniere, allestimenti personalizzati: descrivi la tua idea e ricevi proposte da artigiani italiani
                  </p>
                </div>
              </div>
              <Link to="/feste-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-pink-600 hover:bg-pink-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="feste-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Organizza la tua festa su misura
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Battesimi Custom Request CTA Banner */}
      {(subsubcategory === 'battesimi' || subcategory === 'battesimi' || category === 'battesimi' ||
        subsubcategory?.includes('battesimo') || subcategory?.includes('battesimo') || category?.includes('battesimo')) && (
        <section className="bg-gradient-to-r from-sky-500 to-blue-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Baby className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi bomboniere e inviti per il battesimo?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Ceramiche dipinte a mano, partecipazioni calligrafiche: descrivi la tua idea e ricevi proposte
                  </p>
                </div>
              </div>
              <Link to="/battesimi-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-sky-600 hover:bg-sky-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="battesimi-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Organizza il battesimo perfetto
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Comunioni e Cresime Custom Request CTA Banner */}
      {(subsubcategory === 'comunioni-cresime' || subcategory === 'comunioni-cresime' || category === 'comunioni-cresime' ||
        subsubcategory?.includes('comunione') || subcategory?.includes('comunione') || category?.includes('comunione') ||
        subsubcategory?.includes('cresima') || subcategory?.includes('cresima') || category?.includes('cresima')) && (
        <section className="bg-gradient-to-r from-amber-500 to-yellow-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Church className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi bomboniere per la Comunione o Cresima?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Ceramiche, legno inciso, calligrafia: descrivi la tua idea e ricevi proposte da artigiani italiani
                  </p>
                </div>
              </div>
              <Link to="/comunioni-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-amber-600 hover:bg-amber-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="comunioni-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Organizza la cerimonia perfetta
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Lauree Custom Request CTA Banner */}
      {(subsubcategory === 'lauree' || subcategory === 'lauree' || category === 'lauree' ||
        subsubcategory === 'laurea' || subcategory === 'laurea' || category === 'laurea') && (
        <section className="bg-gradient-to-r from-red-500 to-orange-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <GraduationCap className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi bomboniere e gadget per la laurea?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Corone d'alloro, inviti personalizzati, allestimenti a tema: descrivi la tua idea e ricevi proposte
                  </p>
                </div>
              </div>
              <Link to="/lauree-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-red-600 hover:bg-red-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="lauree-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Organizza la festa di laurea
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Falegnameria Generic CTA Banner - ONLY for parent category (under Artigianato/oggetti) */}
      {subcategory === 'falegnameria' && !subsubcategory && (
        <section className="bg-gradient-to-r from-amber-600 to-stone-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Hammer className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi un pezzo in legno artigianale?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Taglieri, cornici, sculture, cofanetti: descrivi la tua idea e ricevi proposte da falegnami italiani
                  </p>
                </div>
              </div>
              <Link to="/falegnameria-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-amber-600 hover:bg-amber-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="falegnameria-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Richiedi falegnameria su misura
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Stampa 3D Custom Request CTA Banner - Covers parent and all children */}
      {(category === 'stampa-3d' || subcategory === 'stampa-3d' || 
        category === 'decorazioni-3d' || subcategory === 'decorazioni-3d' || subsubcategory === 'decorazioni-3d' ||
        category === 'gioielli-3d' || subcategory === 'gioielli-3d' || subsubcategory === 'gioielli-3d' ||
        category === 'lampade-3d' || subcategory === 'lampade-3d' || subsubcategory === 'lampade-3d' ||
        category === 'sculture-3d' || subcategory === 'sculture-3d' || subsubcategory === 'sculture-3d' ||
        category === 'action-figures-3d' || subcategory === 'action-figures-3d' || subsubcategory === 'action-figures-3d' ||
        category === 'portachiavi-3d' || subcategory === 'portachiavi-3d' || subsubcategory === 'portachiavi-3d' ||
        category === 'personalizzazioni-3d' || subcategory === 'personalizzazioni-3d' || subsubcategory === 'personalizzazioni-3d' ||
        category === 'fioriere-vasi-3d' || subcategory === 'fioriere-vasi-3d' || subsubcategory === 'fioriere-vasi-3d') && (
        <section className="bg-gradient-to-r from-cyan-600 to-blue-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Printer className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Hai un progetto di stampa 3D in mente?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Action figure, lampade, decorazioni, prototipi: descrivi la tua idea e ricevi proposte da maker italiani
                  </p>
                </div>
              </div>
              <Link to="/stampa-3d-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-cyan-600 hover:bg-cyan-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="stampa-3d-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Richiedi stampa 3D su misura
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Business Custom Request CTA Banner - Covers parent and all children */}
      {(category === 'business' || subcategory === 'business' || subsubcategory === 'business' ||
        category === 'gadget-aziendali' || subcategory === 'gadget-aziendali' || subsubcategory === 'gadget-aziendali' ||
        category === 'premi-aziendali' || subcategory === 'premi-aziendali' || subsubcategory === 'premi-aziendali' ||
        category === 'targhe-aziendali' || subcategory === 'targhe-aziendali' || subsubcategory === 'targhe-aziendali' ||
        category === 'trofei' || subcategory === 'trofei' || subsubcategory === 'trofei' ||
        category === 'packaging-aziendale' || subcategory === 'packaging-aziendale' || subsubcategory === 'packaging-aziendale' ||
        category === 'allestimenti-aziendali' || subcategory === 'allestimenti-aziendali' || subsubcategory === 'allestimenti-aziendali' ||
        category === 'regali-corporate' || subcategory === 'regali-corporate' || subsubcategory === 'regali-corporate' ||
        category === 'penne-cancelleria-aziendali' || subcategory === 'penne-cancelleria-aziendali' || subsubcategory === 'penne-cancelleria-aziendali' ||
        category === 'agende-blocknotes-aziendali' || subcategory === 'agende-blocknotes-aziendali' || subsubcategory === 'agende-blocknotes-aziendali' ||
        category === 'accessori-tech-aziendali' || subcategory === 'accessori-tech-aziendali' || subsubcategory === 'accessori-tech-aziendali' ||
        category === 'abbigliamento-brandizzato' || subcategory === 'abbigliamento-brandizzato' || subsubcategory === 'abbigliamento-brandizzato' ||
        category === 'borse-shopper-aziendali' || subcategory === 'borse-shopper-aziendali' || subsubcategory === 'borse-shopper-aziendali' ||
        category === 'medaglie-aziendali' || subcategory === 'medaglie-aziendali' || subsubcategory === 'medaglie-aziendali' ||
        category === 'coppe-aziendali' || subcategory === 'coppe-aziendali' || subsubcategory === 'coppe-aziendali' ||
        category === 'statuette-premio' || subcategory === 'statuette-premio' || subsubcategory === 'statuette-premio' ||
        category === 'attestati-diplomi' || subcategory === 'attestati-diplomi' || subsubcategory === 'attestati-diplomi' ||
        category === 'targhe-metallo' || subcategory === 'targhe-metallo' || subsubcategory === 'targhe-metallo' ||
        category === 'targhe-legno' || subcategory === 'targhe-legno' || subsubcategory === 'targhe-legno' ||
        category === 'targhe-plexiglass' || subcategory === 'targhe-plexiglass' || subsubcategory === 'targhe-plexiglass' ||
        category === 'targhe-commemorative' || subcategory === 'targhe-commemorative' || subsubcategory === 'targhe-commemorative' ||
        category === 'trofei-cristallo' || subcategory === 'trofei-cristallo' || subsubcategory === 'trofei-cristallo' ||
        category === 'trofei-metallo' || subcategory === 'trofei-metallo' || subsubcategory === 'trofei-metallo' ||
        category === 'trofei-sportivi' || subcategory === 'trofei-sportivi' || subsubcategory === 'trofei-sportivi' ||
        category === 'trofei-legno' || subcategory === 'trofei-legno' || subsubcategory === 'trofei-legno' ||
        category === 'scatole-regalo-aziendali' || subcategory === 'scatole-regalo-aziendali' || subsubcategory === 'scatole-regalo-aziendali' ||
        category === 'shopper-personalizzate-business' || subcategory === 'shopper-personalizzate-business' || subsubcategory === 'shopper-personalizzate-business' ||
        category === 'packaging-ecologico' || subcategory === 'packaging-ecologico' || subsubcategory === 'packaging-ecologico' ||
        category === 'nastri-accessori-packaging' || subcategory === 'nastri-accessori-packaging' || subsubcategory === 'nastri-accessori-packaging' ||
        category === 'stand-fieristici' || subcategory === 'stand-fieristici' || subsubcategory === 'stand-fieristici' ||
        category === 'espositori-display' || subcategory === 'espositori-display' || subsubcategory === 'espositori-display' ||
        category === 'insegne-totem' || subcategory === 'insegne-totem' || subsubcategory === 'insegne-totem' ||
        category === 'arredi-eventi' || subcategory === 'arredi-eventi' || subsubcategory === 'arredi-eventi' ||
        category === 'cesti-regalo-gourmet' || subcategory === 'cesti-regalo-gourmet' || subsubcategory === 'cesti-regalo-gourmet' ||
        category === 'cofanetti-vino-aziendali' || subcategory === 'cofanetti-vino-aziendali' || subsubcategory === 'cofanetti-vino-aziendali' ||
        category === 'accessori-lusso-corporate' || subcategory === 'accessori-lusso-corporate' || subsubcategory === 'accessori-lusso-corporate' ||
        category === 'regali-personalizzati-corporate' || subcategory === 'regali-personalizzati-corporate' || subsubcategory === 'regali-personalizzati-corporate' ||
        category === 'welcome-kit-dipendenti' || subcategory === 'welcome-kit-dipendenti' || subsubcategory === 'welcome-kit-dipendenti') && (
        <section className="bg-gradient-to-r from-slate-700 to-blue-700 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Briefcase className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi articoli business personalizzati?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Gadget aziendali, premi, targhe, trofei, packaging e regali corporate Made in Italy
                  </p>
                </div>
              </div>
              <Link to="/business-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-slate-700 hover:bg-slate-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="business-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Richiedi preventivo business
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Tessile e Fibre Custom Request CTA Banner - Covers parent and all children */}
      {(category === 'tessile-fibre' || subcategory === 'tessile-fibre' || subsubcategory === 'tessile-fibre' ||
        category === 'cucito-sartoria' || subcategory === 'cucito-sartoria' || subsubcategory === 'cucito-sartoria' ||
        category === 'ricamo' || subcategory === 'ricamo' || subsubcategory === 'ricamo' ||
        category === 'maglia-uncinetto' || subcategory === 'maglia-uncinetto' || subsubcategory === 'maglia-uncinetto' ||
        category === 'macrame' || subcategory === 'macrame' || subsubcategory === 'macrame' ||
        category === 'tessitura' || subcategory === 'tessitura' || subsubcategory === 'tessitura' ||
        category === 'feltro-lana-cardata' || subcategory === 'feltro-lana-cardata' || subsubcategory === 'feltro-lana-cardata' ||
        category === 'tintura-stampa-tessuto' || subcategory === 'tintura-stampa-tessuto' || subsubcategory === 'tintura-stampa-tessuto' ||
        category === 'accessori-tessili' || subcategory === 'accessori-tessili' || subsubcategory === 'accessori-tessili' || subsubsubcategory === 'accessori-tessili' ||
        category === 'complementi-arredo-tessuto' || subcategory === 'complementi-arredo-tessuto' || subsubcategory === 'complementi-arredo-tessuto' || subsubsubcategory === 'complementi-arredo-tessuto' ||
        category === 'creazioni-tessili-su-misura' || subcategory === 'creazioni-tessili-su-misura' || subsubcategory === 'creazioni-tessili-su-misura' || subsubsubcategory === 'creazioni-tessili-su-misura' ||
        category === 'ricamo-a-mano' || subcategory === 'ricamo-a-mano' || subsubcategory === 'ricamo-a-mano' || subsubsubcategory === 'ricamo-a-mano' ||
        category === 'punto-croce' || subcategory === 'punto-croce' || subsubcategory === 'punto-croce' || subsubsubcategory === 'punto-croce' ||
        category === 'ricamo-moderno' || subcategory === 'ricamo-moderno' || subsubcategory === 'ricamo-moderno' || subsubsubcategory === 'ricamo-moderno' ||
        category === 'lavori-maglia' || subcategory === 'lavori-maglia' || subsubcategory === 'lavori-maglia' || subsubsubcategory === 'lavori-maglia' ||
        category === 'uncinetto' || subcategory === 'uncinetto' || subsubcategory === 'uncinetto' || subsubsubcategory === 'uncinetto' ||
        category === 'amigurumi' || subcategory === 'amigurumi' || subsubcategory === 'amigurumi' || subsubsubcategory === 'amigurumi' ||
        category === 'decorazioni-macrame' || subcategory === 'decorazioni-macrame' || subsubcategory === 'decorazioni-macrame' || subsubsubcategory === 'decorazioni-macrame' ||
        category === 'portavasi-macrame' || subcategory === 'portavasi-macrame' || subsubcategory === 'portavasi-macrame' || subsubsubcategory === 'portavasi-macrame' ||
        category === 'arazzi-macrame' || subcategory === 'arazzi-macrame' || subsubcategory === 'arazzi-macrame' || subsubsubcategory === 'arazzi-macrame' ||
        category === 'arazzi-tessuti' || subcategory === 'arazzi-tessuti' || subsubcategory === 'arazzi-tessuti' || subsubsubcategory === 'arazzi-tessuti' ||
        category === 'tessuti-artigianali' || subcategory === 'tessuti-artigianali' || subsubcategory === 'tessuti-artigianali' || subsubsubcategory === 'tessuti-artigianali' ||
        category === 'telaio-manuale' || subcategory === 'telaio-manuale' || subsubcategory === 'telaio-manuale' || subsubsubcategory === 'telaio-manuale' ||
        category === 'oggetti-decorativi-feltro' || subcategory === 'oggetti-decorativi-feltro' || subsubcategory === 'oggetti-decorativi-feltro' || subsubsubcategory === 'oggetti-decorativi-feltro' ||
        category === 'accessori-feltro' || subcategory === 'accessori-feltro' || subsubcategory === 'accessori-feltro' || subsubsubcategory === 'accessori-feltro' ||
        category === 'sculture-tessili-feltro' || subcategory === 'sculture-tessili-feltro' || subsubcategory === 'sculture-tessili-feltro' || subsubsubcategory === 'sculture-tessili-feltro' ||
        category === 'tinture-naturali' || subcategory === 'tinture-naturali' || subsubcategory === 'tinture-naturali' || subsubsubcategory === 'tinture-naturali' ||
        category === 'batik' || subcategory === 'batik' || subsubcategory === 'batik' || subsubsubcategory === 'batik' ||
        category === 'block-printing' || subcategory === 'block-printing' || subsubcategory === 'block-printing' || subsubsubcategory === 'block-printing') && (
        <section className="bg-gradient-to-r from-rose-600 to-amber-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Scissors className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi una creazione tessile su misura?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Maglia, ricamo, macramè, tessitura e molto altro: artigiani italiani pronti a creare per te
                  </p>
                </div>
              </div>
              <Link to="/tessile-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-rose-600 hover:bg-rose-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="tessile-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Richiedi preventivo tessile
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Taglieri Custom Request CTA Banner */}
      {(subsubcategory === 'taglieri' || subcategory === 'taglieri' || category === 'taglieri' ||
        subsubcategory === 'taglieri-legno' || subcategory === 'taglieri-legno' || category === 'taglieri-legno') && (
        <section className="bg-gradient-to-r from-amber-600 to-orange-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Slice className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi un tagliere artigianale su misura?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Ulivo, noce, acacia con incisioni personalizzate: descrivi la tua idea e ricevi proposte
                  </p>
                </div>
              </div>
              <Link to="/taglieri-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-amber-600 hover:bg-amber-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="taglieri-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Crea il tuo tagliere su misura
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Cornici Custom Request CTA Banner */}
      {(subsubcategory === 'cornici' || subcategory === 'cornici' || category === 'cornici' ||
        subsubcategory === 'cornici-legno' || subcategory === 'cornici-legno' || category === 'cornici-legno' ||
        subsubcategory === 'cornici-artigianali' || subcategory === 'cornici-artigianali' || category === 'cornici-artigianali') && (
        <section className="bg-gradient-to-r from-stone-600 to-amber-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Frame className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi una cornice artigianale su misura?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Intagli, dorature, legni pregiati: descrivi la tua idea e ricevi proposte
                  </p>
                </div>
              </div>
              <Link to="/cornici-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-stone-600 hover:bg-stone-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="cornici-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Crea la tua cornice su misura
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Sculture e Intagli Custom Request CTA Banner */}
      {(subsubcategory === 'sculture-intagli' || subcategory === 'sculture-intagli' || category === 'sculture-intagli' ||
        subsubcategory === 'sculture-e-intagli' || subcategory === 'sculture-e-intagli' || category === 'sculture-e-intagli') && (
        <section className="bg-gradient-to-r from-amber-600 to-stone-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Hammer className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi una scultura in legno su misura?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Figure sacre, bassorilievi, intagli: descrivi la tua idea e ricevi proposte da scultori
                  </p>
                </div>
              </div>
              <Link to="/sculture-intagli-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-amber-600 hover:bg-amber-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="sculture-intagli-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Commissiona la tua scultura
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Oggetti Decorativi Custom Request CTA Banner */}
      {(subsubcategory === 'oggetti-decorativi' || subcategory === 'oggetti-decorativi' || category === 'oggetti-decorativi' ||
        subsubcategory === 'oggetti-decorativi-legno' || subcategory === 'oggetti-decorativi-legno' || category === 'oggetti-decorativi-legno') && (
        <section className="bg-gradient-to-r from-emerald-600 to-amber-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Lamp className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi un oggetto decorativo in legno?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Vasi, ciotole, orologi, candelabri: descrivi la tua idea e ricevi proposte
                  </p>
                </div>
              </div>
              <Link to="/oggetti-decorativi-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="oggetti-decorativi-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Crea il tuo oggetto decorativo
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Cofanetti e Scatole Custom Request CTA Banner */}
      {(subsubcategory === 'cofanetti-scatole' || subcategory === 'cofanetti-scatole' || category === 'cofanetti-scatole' ||
        subsubcategory === 'cofanetti-e-scatole' || subcategory === 'cofanetti-e-scatole' || category === 'cofanetti-e-scatole') && (
        <section className="bg-gradient-to-r from-violet-600 to-amber-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Box className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi un cofanetto artigianale su misura?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Portagioie, portaorologi, scatole dei ricordi: descrivi la tua idea e ricevi proposte
                  </p>
                </div>
              </div>
              <Link to="/cofanetti-scatole-su-misura">
                <Button 
                  size="lg" 
                  className="bg-white text-violet-600 hover:bg-violet-50 font-semibold shadow-lg whitespace-nowrap"
                  data-testid="cofanetti-scatole-cta-banner-button"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Crea il tuo cofanetto su misura
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Arte Generic CTA Banner (for Arte category without specific subcategory) */}
      {(category === 'arte' && !subcategory) && (
        <section className="bg-gradient-to-r from-violet-600 to-fuchsia-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Palette className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Cerchi un'opera d'arte su commissione?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Dipinti, sculture, ceramiche: descrivi la tua idea e ricevi proposte da artisti italiani
                  </p>
                </div>
              </div>
              <Link to="/arte-su-commissione">
                <Button 
                  size="lg" 
                  className="bg-white text-violet-600 hover:bg-violet-50 font-semibold shadow-lg whitespace-nowrap"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Commissiona la tua opera
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Arte subcategories CTA (for subcategories without dedicated landing) */}
      {category === 'arte' && subcategory && !['pittura', 'scultura'].includes(subcategory) && (
        <section className="bg-gradient-to-r from-violet-600 to-fuchsia-600 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4 text-white">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Palette className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold">Vuoi commissionare un'opera personalizzata?</h2>
                  <p className="text-white/90 text-sm md:text-base">
                    Descrivi la tua idea e ricevi proposte da artisti e artigiani italiani
                  </p>
                </div>
              </div>
              <Link to="/arte-su-commissione">
                <Button 
                  size="lg" 
                  className="bg-white text-violet-600 hover:bg-violet-50 font-semibold shadow-lg whitespace-nowrap"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Commissiona la tua opera
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Subcategories Grid */}
      {displaySubcategories.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">
            Esplora le categorie
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {displaySubcategories.map((sub) => (
              <Link 
                key={sub.id} 
                to={getSubcategoryLink(sub.id, sub.name)}
                className="group"
              >
                <motion.div
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="aspect-[4/3] relative">
                      <img 
                        src={getSubcategoryImage(sub)}
                        alt={`${sub.name} - Artigianato italiano`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="text-white font-semibold text-lg">{sub.name}</h3>
                        <p className="text-white/80 text-sm">
                          {subcategoryCounts[sub.id] || 0} prodotti
                        </p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Products Section */}
      <section className="max-w-7xl mx-auto px-4 py-8 pb-16">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-800">
              {subsubcategory 
                ? `Prodotti ${subsubcategoryInfo?.name || subsubcategory}` 
                : subcategory 
                  ? `Prodotti ${subcategoryInfo?.name || subcategory}` 
                  : 'Prodotti in evidenza'}
            </h2>
            <p className="text-slate-600">
              {totalCount} prodotti trovati
            </p>
          </div>
          <Link to="/esplora">
            <Button variant="outline" className="gap-2">
              <Filter className="w-4 h-4" />
              Vedi tutti
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#008080]" />
          </div>
        ) : products.length > 0 ? (
          <div key={`products-grid-${isLaunchMode}`} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {products.map((product, index) => {
              const mainImgIndex = product.customization_options?.main_image_index || 0;
              const mainImage = optimizeImageUrl(product.images?.[mainImgIndex] || product.images?.[0], 500);
              
              return (
                <Link
                  key={product.id}
                  to={productUrl(product)}
                  className="block"
                  data-testid={`category-product-card-${index}`}
                >
                  <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full border-slate-100">
                    <div
                      className="aspect-square bg-white relative"
                      style={{
                        backgroundImage: mainImage ? `url(${mainImage})` : undefined,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                    >
                      {!mainImage && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <ShoppingBag className="w-10 h-10 text-slate-300" />
                        </div>
                      )}
                      {product.category && (
                        <Badge className="absolute top-2 left-2 bg-white/90 text-slate-700 text-xs">
                          {getChildCategoryName(product.customization_options?.categoryPath) ||
                           product.customization_options?.categoryPath?.sub ||
                           product.category}
                        </Badge>
                      )}
                      {/* Availability + customization badges - bottom left */}
                      {(() => {
                        const { isAvailable, isOnDemand } = getProductAvailability(product);
                        const hasCustomizations = product.customization_options?.customizations?.length > 0;
                        if (!isAvailable) {
                          return (
                            <Badge className="absolute bottom-2 left-2 bg-red-500 text-white text-xs">
                              Esaurito
                            </Badge>
                          );
                        }
                        if (hasCustomizations) {
                          return (
                            <Badge className="absolute bottom-2 left-2 bg-[#DAA520] text-white text-xs">
                              Personalizzabile
                            </Badge>
                          );
                        }
                        if (isOnDemand) {
                          return (
                            <Badge className="absolute bottom-2 left-2 bg-[#008080] text-white text-xs">
                              Su commissione
                            </Badge>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div className="p-3">
                      <h3 className="font-medium text-sm line-clamp-1 mb-1">{product.title}</h3>
                      
                      {/* Crafter info */}
                      {product.profiles && (
                        <div className="flex items-center gap-1.5 mb-2">
                          <div
                            className="w-5 h-5 rounded-full bg-slate-200"
                            style={{
                              backgroundImage: product.profiles.avatar_url ? `url(${optimizeImageUrl(product.profiles.avatar_url, 48)})` : undefined,
                              backgroundSize: 'cover',
                            }}
                          ></div>
                          <span className="text-xs text-slate-500 truncate">
                            {product.profiles.full_name || 'Artigiano'}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        {(() => {
                          const crafterDiscount = product.customization_options?.crafterDiscount || 0;
                          const hasDiscount = crafterDiscount > 0 || isLaunchMode;
                          const originalPrice = calculateFullPrice(product.base_price);
                          const finalPrice = calculateFinalPrice(product.base_price, crafterDiscount, isLaunchMode);
                          const totalDiscountPercent = getTotalDiscountPercentage(crafterDiscount, isLaunchMode);
                          
                          if (hasDiscount && originalPrice !== finalPrice) {
                            return (
                              <>
                                <div className="flex flex-col">
                                  <span className="text-xs text-slate-400 line-through">
                                    €{originalPrice.toFixed(2)}
                                  </span>
                                  <span className="text-base font-bold text-[#008080]">
                                    €{finalPrice.toFixed(2)}
                                  </span>
                                </div>
                                <Badge className={`${crafterDiscount > 0 && isLaunchMode ? 'bg-gradient-to-r from-orange-500 to-green-500' : crafterDiscount > 0 ? 'bg-orange-500' : 'bg-green-500'} text-white text-[10px] px-1.5 py-0.5`}>
                                  -{totalDiscountPercent}%
                                </Badge>
                              </>
                            );
                          }
                          return (
                            <span className="text-base font-bold text-[#008080]">
                              €{finalPrice.toFixed(2)}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 max-w-2xl mx-auto">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            {isLaunchMode ? (
              <>
                <h3 className="text-xl font-semibold text-slate-700 mb-3">
                  Prodotti in arrivo
                </h3>
                <p className="text-slate-500 mb-4">
                  Rarifex è appena nato e in questa categoria non sono ancora presenti prodotti dei nostri artigiani.
                  Continua a esplorare il catalogo o torna a trovarci presto!
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mt-6">
                  <Link to="/esplora">
                    <Button variant="outline" className="gap-2">
                      <ArrowRight className="w-4 h-4" />
                      Esplora altre categorie
                    </Button>
                  </Link>
                  <Link to={user ? "/crafter/dashboard?tab=artigiano" : "/plans"}>
                    <Button className="bg-[#008080] hover:bg-[#006666] gap-2">
                      <Sparkles className="w-4 h-4" />
                      Sei un artigiano? Inizia a vendere
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-xl font-semibold text-slate-700 mb-2">
                  Nessun prodotto ancora
                </h3>
                <p className="text-slate-500">
                  I nostri artigiani stanno preparando nuovi prodotti per questa categoria.
                </p>
              </>
            )}
          </div>
        )}
      </section>

      {/* SEO Footer Content Section */}
      <section className="bg-white py-12 border-t">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">
            {seoData.footerTitle}
          </h2>
          <div className="prose prose-slate max-w-none">
            {seoData.footerDescription ? (
              <p>{seoData.footerDescription}</p>
            ) : (
              <>
                <p>
                  Su Rarifex trovi una selezione curata di {currentName.toLowerCase()} realizzati 
                  interamente a mano da artigiani italiani. Ogni pezzo è unico, creato con 
                  tecniche tradizionali tramandate di generazione in generazione.
                </p>
                <p>
                  I nostri artigiani selezionano personalmente i materiali migliori per garantire 
                  qualità e durabilità. Acquistando su Rarifex, sostieni l'artigianato italiano 
                  e porti a casa un pezzo di autentica tradizione Made in Italy.
                </p>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
